import { useEffect, useState } from 'react';
import { Sheet } from '../../components/Overlay.tsx';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { ErrorState, Loading } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { setShareTargets, shareTargets } from '../../api/share.ts';
import type { ShareTarget } from '../../domain.ts';

/**
 * C4 의 공유 — 노트를 누구에게 보여 줄지 고릅니다.
 *
 * **차단한 상대는 목록에 없습니다.** 이미 가 있더라도 고를 수 있는 자리에는
 * 두지 않습니다 — 다시 켤 수 있으면 차단이 차단이 아닙니다. 이미 간 것을
 * 거두는 일은 차단할 때 따로 묻습니다 (D3).
 *
 * **베끼지 않습니다.** 공유한 뒤 원본을 고치면 받은 쪽도 바뀝니다 —
 * 응답완료로 바꾼 노트가 남의 방에 '기도중'으로 남지 않아야 합니다.
 *
 * 켠 것만 남고 끈 것은 떨어집니다. 적용을 눌러야 반영되고, 손잡이를 끌거나
 * 어두운 배경으로 닫으면 고르기 전으로 되돌아갑니다 — 필터 시트(C1-4)와
 * 같은 규칙입니다.
 */

interface Props {
  noteId: string;
  /** 비공개 노트는 공유할 수 없습니다 — 먼저 Private 를 꺼야 합니다 */
  isPrivate: boolean;
  onClose: () => void;
}

export function ShareSheet({ noteId, isPrivate, onClose }: Props) {
  const [targets, setTargets] = useState<ShareTarget[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    shareTargets(noteId)
      .then((rows) => {
        if (!alive) return;
        setTargets(rows);
        setPicked(new Set(rows.filter((r) => r.shared).map((r) => r.userId)));
      })
      .catch((e: unknown) => {
        if (alive) setFailure(e instanceof ApiError ? e.message : '불러오지 못했습니다.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [noteId]);

  const toggle = (userId: string) =>
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const apply = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await setShareTargets(noteId, [...picked]);
      onClose();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '공유하지 못했습니다.');
      setBusy(false);
    }
  };

  /* 비공개면 고를 것이 없습니다 — 먼저 꺼야 한다고만 말합니다 */
  if (isPrivate) {
    return (
      <Sheet title="공유" onClose={onClose}>
        <div className="empty">
          <p className="empty__title">비공개 노트는 공유할 수 없습니다</p>
          <p className="empty__hint">
            설정에서 <b>Private</b> 를 끄면 친구에게 보낼 수 있습니다.
          </p>
        </div>
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          닫기
        </button>
      </Sheet>
    );
  }

  return (
    <Sheet title="공유" onClose={onClose}>
      <div className="sheet__scroll">
        {loading && <Loading label="친구를 불러오는 중" />}
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        {targets !== null && targets.length === 0 && (
          <div className="empty">
            <p className="empty__title">보낼 친구가 없습니다</p>
            <p className="empty__hint">
              Prayer Partner 의 ＋ 로 초대 코드를 주고받으면 친구가 됩니다.
            </p>
          </div>
        )}

        {targets?.map((t) => {
          const on = picked.has(t.userId);
          return (
            <button
              key={t.userId}
              type="button"
              className="row"
              aria-pressed={on}
              onClick={() => toggle(t.userId)}
            >
              <Avatar name={t.name ?? '?'} seed={t.userId} />
              <span className="row__grow">
                <span className="row__name">{t.name ?? '이름 없음'}</span>
              </span>
              <span className={on ? 'chk chk--on' : 'chk'}>
                {on && <Icon name="check" size={13} />}
              </span>
            </button>
          );
        })}
      </div>

      {/* 끈 것은 떨어집니다. 그 사실을 누르기 전에 적어 둡니다 */}
      <p className="hint">
        켠 사람에게만 남습니다 — 껐던 사람에게서는 사라집니다. 공유한 뒤 원본을 고치면 받은
        쪽에도 그대로 반영됩니다.
      </p>

      <button
        type="button"
        className="btn btn--primary"
        disabled={busy || loading}
        onClick={() => void apply()}
      >
        {busy ? '저장 중…' : '적용'}
      </button>
    </Sheet>
  );
}
